import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DownOutlined,
  GiftOutlined,
  TrophyOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { Button, Card, Col, Row, Skeleton, Space, Statistic, Tag, Typography } from "antd";
import dayjs from "dayjs";
import * as React from "react";
import {
  getQuestTypeLabel,
  getRewardTypeLabel,
  QuestDetail,
  QuestType,
  RewardType,
  useQuestPipeline,
} from "src/hooks/affiliate";

const { Text } = Typography;

const formatNumber = (n?: number) => (n == null ? "-" : n.toLocaleString());

const QUEST_TYPE_COLOR = "#1890ff";
const REWARD_COLOR: Record<RewardType, string> = {
  [RewardType.FIXED_MONEY]: "#52c41a",
  [RewardType.BONUS]: "#faad14",
  [RewardType.COMMISSION_PERCENTAGE]: "#13c2c2",
};

interface BreakdownRowProps {
  label: React.ReactNode;
  count: number;
  total: number;
  color?: string;
}

const BreakdownRow: React.FunctionComponent<BreakdownRowProps> = ({
  label,
  count,
  total,
  color = QUEST_TYPE_COLOR,
}) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
        <span>{label}</span>
        <Text type="secondary">
          {count} ({pct.toFixed(0)}%)
        </Text>
      </div>
      <div
        style={{
          background: "#f5f5f5",
          height: 6,
          borderRadius: 3,
          overflow: "hidden",
          marginTop: 2,
        }}
      >
        <div
          style={{
            background: color,
            width: `${pct}%`,
            height: "100%",
            transition: "width 0.3s",
          }}
        />
      </div>
    </div>
  );
};

interface QuestDashboardProps {
  quests: QuestDetail[];
}

const QUEST_TYPES_PREVIEW_COUNT = 3;

export const QuestDashboard: React.FunctionComponent<QuestDashboardProps> = ({ quests }) => {
  const { data: pipeline, isFetching: pipelineLoading } = useQuestPipeline();
  const [questTypesExpanded, setQuestTypesExpanded] = React.useState(false);

  // Client-side KPIs from quest list
  const total = quests.length;
  const activeCount = quests.filter((q) => q.isActive).length;
  const bonusCount = quests.filter((q) => q.rewardType === RewardType.BONUS).length;
  const expiringCount = quests.filter((q) => {
    if (!q.endDate) return false;
    const diff = dayjs(q.endDate).diff(dayjs(), "day");
    return q.isActive && diff >= 0 && diff < 7;
  }).length;

  // Quest type breakdown
  const byQuestType: Record<string, number> = {};
  for (const q of quests) {
    byQuestType[q.questType] = (byQuestType[q.questType] || 0) + 1;
  }
  const questTypeRows = Object.entries(byQuestType)
    .sort((a, b) => b[1] - a[1])
    .map(([type, count]) => ({ type, count }));

  // Reward type breakdown
  const byRewardType: Record<string, number> = {};
  for (const q of quests) {
    byRewardType[q.rewardType] = (byRewardType[q.rewardType] || 0) + 1;
  }
  const rewardTypeOrder = [
    RewardType.FIXED_MONEY,
    RewardType.BONUS,
    RewardType.COMMISSION_PERCENTAGE,
  ];

  // Pipeline percentages
  const pipelineTotal = pipeline?.total ?? 0;
  const stages = [
    { key: "IN_PROGRESS", label: "IN_PROGRESS", count: pipeline?.inProgress ?? 0, color: "#1890ff" },
    { key: "COMPLETED", label: "COMPLETED", count: pipeline?.completed ?? 0, color: "#52c41a" },
    { key: "REWARDED", label: "REWARDED", count: pipeline?.rewarded ?? 0, color: "#faad14" },
  ];

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col xs={12} md={6}>
        <Card size="small">
          <Statistic
            title="Total Quests"
            value={formatNumber(total)}
            prefix={<TrophyOutlined style={{ color: "#1890ff" }} />}
          />
        </Card>
      </Col>
      <Col xs={12} md={6}>
        <Card size="small">
          <Statistic
            title="Active Quests"
            value={formatNumber(activeCount)}
            prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
            suffix={
              <Text type="secondary" style={{ fontSize: 12 }}>
                / {total}
              </Text>
            }
          />
        </Card>
      </Col>
      <Col xs={12} md={6}>
        <Card size="small">
          <Statistic
            title="Bonus Reward Quests"
            value={formatNumber(bonusCount)}
            prefix={<GiftOutlined style={{ color: "#faad14" }} />}
            suffix={
              <Text type="secondary" style={{ fontSize: 12 }}>
                / {total}
              </Text>
            }
          />
        </Card>
      </Col>
      <Col xs={12} md={6}>
        <Card size="small">
          <Statistic
            title="Expiring ใน 7 วัน"
            value={formatNumber(expiringCount)}
            prefix={<ClockCircleOutlined style={{ color: "#fa541c" }} />}
            suffix={
              <Text type="secondary" style={{ fontSize: 12 }}>
                quest
              </Text>
            }
          />
        </Card>
      </Col>

      <Col xs={24} md={12}>
        <Card
          size="small"
          title={
            <Space>
              <span>Quest Types</span>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ({questTypeRows.length} ชนิด)
              </Text>
            </Space>
          }
          extra={
            questTypeRows.length > QUEST_TYPES_PREVIEW_COUNT && (
              <Button
                type="link"
                size="small"
                onClick={() => setQuestTypesExpanded((v) => !v)}
                icon={questTypesExpanded ? <UpOutlined /> : <DownOutlined />}
              >
                {questTypesExpanded
                  ? "ย่อ"
                  : `แสดงทั้งหมด (${questTypeRows.length})`}
              </Button>
            )
          }
        >
          {questTypeRows.length === 0 ? (
            <Text type="secondary">ยังไม่มี quest</Text>
          ) : (
            (questTypesExpanded
              ? questTypeRows
              : questTypeRows.slice(0, QUEST_TYPES_PREVIEW_COUNT)
            ).map((r) => (
              <BreakdownRow
                key={r.type}
                label={<Tag style={{ marginRight: 0 }}>{getQuestTypeLabel(r.type)}</Tag>}
                count={r.count}
                total={total}
              />
            ))
          )}
        </Card>
      </Col>

      <Col xs={24} md={12}>
        <Card size="small" title="Reward Types">
          {total === 0 ? (
            <Text type="secondary">ยังไม่มี quest</Text>
          ) : (
            rewardTypeOrder.map((rt) => (
              <BreakdownRow
                key={rt}
                label={
                  <Tag color={REWARD_COLOR[rt]} style={{ marginRight: 0 }}>
                    {getRewardTypeLabel(rt)}
                  </Tag>
                }
                count={byRewardType[rt] || 0}
                total={total}
                color={REWARD_COLOR[rt]}
              />
            ))
          )}
        </Card>
      </Col>

      <Col span={24}>
        <Card
          size="small"
          title={
            <Space>
              <span>Quest Progress Pipeline</span>
              <Text type="secondary" style={{ fontSize: 12 }}>
                (รวม {formatNumber(pipelineTotal)} progress รายการ)
              </Text>
            </Space>
          }
        >
          {pipelineLoading && !pipeline ? (
            <Skeleton active paragraph={{ rows: 1 }} />
          ) : (
            <Row gutter={[16, 12]}>
              {stages.map((s) => {
                const pct = pipelineTotal > 0 ? (s.count / pipelineTotal) * 100 : 0;
                return (
                  <Col key={s.key} xs={24} md={8}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <Tag color={s.color}>{s.label}</Tag>
                      <Text strong>
                        {formatNumber(s.count)}{" "}
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          ({pct.toFixed(1)}%)
                        </Text>
                      </Text>
                    </div>
                    <div
                      style={{
                        background: "#f5f5f5",
                        height: 10,
                        borderRadius: 5,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          background: s.color,
                          width: `${pct}%`,
                          height: "100%",
                          transition: "width 0.3s",
                        }}
                      />
                    </div>
                  </Col>
                );
              })}
            </Row>
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default QuestDashboard;
